import { useEffect, useMemo, useState } from 'react';
import { CapTablesService } from 'api';
import { CompanyInfo } from 'common/types/company';
import { Fund } from 'common/types/fund/fund';
import { Logger } from 'utilities';
import { OwnershipOption } from './useOwnershipOptions.props';
import type { OwnerCashFlowSummary } from '../components/CashFlowSummary/config/types';
import { isInvestedCapitalFlow, isProceedsFlow } from '../data/constants';
import { CashFlowLedgerItem } from '../data/types';

export interface UseServerGrossIRRSummaryParams {
  captableId?: number | null;
  fundList: Fund[] | null | undefined;
  companyInfo?: CompanyInfo | null;
  cashFlows?: CashFlowLedgerItem[];
  ownershipOptions?: OwnershipOption[];
  // Bump whenever the ledger changes so the IRR refetches.
  refreshKey?: unknown;
}

export interface UseServerGrossIRRSummaryReturn {
  owners: Record<string, OwnerCashFlowSummary>;
  firm: OwnerCashFlowSummary;
  ownerNames: Record<string, string>;
  isLoading: boolean;
  error: Error | null;
}

const OWNER_TOKEN = /^(fund|company)-\d+$/;

// Owner token (`fund-<id>` | `company-<id>`) for a ledger row, or null if unrecognised.
const ownerTokenOf = (item: CashFlowLedgerItem): string | null => {
  if (item.owner_fund) {
    return `fund-${item.owner_fund}`;
  }
  if (item.owner_company) {
    return `company-${item.owner_company}`;
  }
  return typeof item.owner === 'string' && OWNER_TOKEN.test(item.owner) ? item.owner : null;
};

const parseIrr = (value: string | number | null | undefined): number | null =>
  value === null || value === undefined ? null : Number(value);

// Feeds the Cash Flow Summary table. Gross IRR comes from the captable
// gross-irrs endpoint (the same computation behind waterfall and fund/firm
// summaries); investments and proceeds are summed client-side from the rows.
export const useServerGrossIRRSummary = ({
  captableId,
  fundList,
  companyInfo,
  cashFlows = [],
  ownershipOptions = [],
  refreshKey,
}: UseServerGrossIRRSummaryParams): UseServerGrossIRRSummaryReturn => {
  const ownerNames = useMemo(() => {
    const names: Record<string, string> = {};
    fundList?.forEach(fund => {
      if (fund?.id && fund.name) {
        names[`fund-${fund.id}`] = fund.name;
      }
    });
    companyInfo?.child_companies?.forEach(child => {
      if (child.company_id && child.company_name) {
        names[`company-${child.company_id}`] = child.company_name;
      }
    });
    ownershipOptions.forEach(option => {
      if (option.value && option.label) {
        names[option.value] = option.label;
      }
    });
    return names;
  }, [ownershipOptions, fundList, companyInfo]);

  const [ownerIrrs, setOwnerIrrs] = useState<Record<string, number | null>>({});
  const [firmIrr, setFirmIrr] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!captableId) {
      return () => {};
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    CapTablesService.apiCapTablesGrossIrrsRead(captableId)
      .then(response => {
        if (cancelled) {
          return;
        }
        const irrs: Record<string, number | null> = {};
        Object.entries(response.funds_gross_irr ?? {}).forEach(([id, value]) => {
          irrs[`fund-${id}`] = parseIrr(value);
        });
        Object.entries(response.companies_gross_irr ?? {}).forEach(([id, value]) => {
          irrs[`company-${id}`] = parseIrr(value);
        });
        setOwnerIrrs(irrs);
        setFirmIrr(parseIrr(response.firm_gross_irr));
      })
      .catch(err => {
        if (cancelled) {
          return;
        }
        Logger.error('Failed to fetch captable gross IRRs', err);
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [captableId, refreshKey]);

  const { owners, firm } = useMemo(() => {
    const byOwner: Record<string, OwnerCashFlowSummary> = {};
    const total: OwnerCashFlowSummary = { investments: 0, proceeds: 0, grossIrr: firmIrr };
    const bucketFor = (token: string) => {
      byOwner[token] ??= { investments: 0, proceeds: 0, grossIrr: ownerIrrs[token] ?? null };
      return byOwner[token];
    };

    Object.keys(ownerIrrs).forEach(bucketFor);

    cashFlows.forEach(item => {
      const token = ownerTokenOf(item);
      const signed = Number(item.amount) || 0;
      if (item.is_excluded_from_calculations || !token || signed === 0) {
        return;
      }
      const add = (bucket: 'investments' | 'proceeds') => {
        bucketFor(token)[bucket] += signed;
        total[bucket] += signed;
      };
      // Typed rows bucket by designation regardless of sign; untyped rows use
      // the sign heuristic. Rows matching neither land nowhere (as on the server).
      if (isInvestedCapitalFlow(item.cash_flow_type, signed)) {
        add('investments');
      } else if (isProceedsFlow(item.cash_flow_type, signed)) {
        add('proceeds');
      }
    });

    return { owners: byOwner, firm: total };
  }, [cashFlows, ownerIrrs, firmIrr]);

  return { owners, firm, ownerNames, isLoading, error };
};
